import { Bounding } from '../../core/models/bounding';
import { Element } from './elements/element';
import { ElementGroup } from './elements/elementGroup';
import { EquipmentElements } from './elements/equipmentElements';
import { Extension, ExtensionType, RoundingParams } from './elements/extension';
import { Figure } from './figures/figure';
import { GlobalSettings } from '../../core/models/globalSettings';

import { Utils } from '../../core/services/utils.service';

const backWallsList: Array<EquipmentElements> = [
		EquipmentElements.BackWall,
		EquipmentElements.CentralPillar,
		EquipmentElements.HookWall,
		EquipmentElements.PerforatedWall
	],
	basesList: Array<EquipmentElements> = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze],
	shelvesList: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf, ...basesList],
	hookWallsList: Array<EquipmentElements> = [EquipmentElements.HookWall, EquipmentElements.HookPanel],
	perforatedWallsList: Array<EquipmentElements> = [EquipmentElements.PerforatedWall, EquipmentElements.Perforation];

//загальні параметри для інструмента
export class Equipment {
	id: number = null;
	name: string = '';
	code: string = '';
	producer: string = '';
	updated_at: any;
	type: number = 0;
	type_name: string;

	private _elements: Array<Element> = [];

	cachedRows: Map<Element, Array<Array<Element>>> = new Map<Element, Array<Array<Element>>>();

	get allElements(): Array<Element> {
		return this._elements;
	}

	get availableElements(): Array<Element> {
		return this._elements.filter((el: Element) => {
			return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
		});
	}

	get bases(): Array<Element> {
		return this._elements.filter((el: Element) => el.parent.is(...basesList));
	}

	constructor(params: any) {
		const { elements, id, name, code, producer, type, type_name, updated_at } = params,
			validatedParams = this.validate({ id, name, code, producer, type, type_name, updated_at });

		Object.assign(this, validatedParams);
		if (elements?.length) this._elements = elements;
	}

	validate(params: any) {
		const validatedParams = {};

		for (let key in params) {
			if (params[key] != null) validatedParams[key] = params[key];
			if (key === 'type') {
				validatedParams['type'] = typeof params[key] === 'object'
					? params[key].id
					: params[key];
			}
		}

		return validatedParams;
	}

	addElement(elements: Element | Element[]): void {
		this._elements = this._elements.concat(elements);
	}

	removeElements(elements: Array<Element>): void {
		const related: Array<Element> = [].concat(...elements.map((el: Element) => this._elements.filter((e) => e.getDepends().includes(el))));
		this._elements = this._elements.filter((el: Element) => !elements.includes(el) && !related.includes(el));
	}

	filterHooks(dependElement: Element) {
		this._elements = this._elements.filter((el: Element) => el.depend !== dependElement);
	}

	filterHooksOnChangeSize(dependElement: Element) {
		this._elements = this._elements.filter((el: Element) => !(el.parent.type == 'Hook' &&
			(el.getBoundingX().max > dependElement.getBoundingX().max ||
				el.getBoundingY().max > dependElement.getBoundingY().max) &&
			el.depend == dependElement));
	}

	getElementById(id: string = null): Element {
		return this.allElements.find((element: Element) => element.id == id) || null;
	}

	getPointedElement(x: number, y: number, representation: string, displayY: number) {
		const pointed_bases = this.bases.filter(base => base.shapes[0].isPointOn(x));

		//TODO: визначити які тут класи беруться в розрахунок і відкинути все лишнє
		let elements = this.allElements.filter(el =>
			pointed_bases.some(b => el.getDepends().includes(b) || el == b) &&
			!['Partition', 'Box', 'SlicedBox'].includes(el.parent.type)
		);

		const max_y_point = Utils.max(elements.map(el => el.getBoundingY().max)) || 0,
			compare_y_point = y == 0 ? max_y_point : this.Height;

		//фікс, якщо погано прив'язалась задня стінка
		if (!elements.some(el => el.parent.group === ElementGroup.AdditionalElement)) {
			elements = elements.concat(
				this.allElements.filter((el: Element) => {
					return el.parent.group === ElementGroup.AdditionalElement && el.shapes[0].isPointOn(x);
				})
			);
		}

		switch (representation) {
			case 'top': {
				if (displayY) {
					elements = elements.filter(el => el.getBoundingY().min <= displayY);
				}

				elements = elements.filter(e => e.parent.type != 'Showcase');

				let hoveredElements = elements
					.filter(el => el.shapes.find(shape => shape.isPointOn(x, null, y)))
					.filter(element => !['SlicedBox', 'Box'].includes(element.parent.type));

				hoveredElements = hoveredElements.sort((a, b) => b.getBoundingY().max - a.getBoundingY().max);

				return hoveredElements[0] || null;
			}
			default: {
				const showcase = elements.find(el => el.parent.type == 'Showcase');

				if (showcase) return showcase;

				let hoveredElements;

				for (let i = 0; i < pointed_bases.length; i++) {
					if (pointed_bases[i].parent.type === EquipmentElements.BaseTrapeze) {
						hoveredElements = elements
							.filter(el => el.shapes.find(shape => shape.isPointOn(x, compare_y_point - y)))
							.sort((a, b) => b.getBoundingZ().max - a.getBoundingZ().max);

						if (hoveredElements[0] && hoveredElements[0].parent.type === EquipmentElements.SideWalls) {
							return elements.find(el => ['BackWall'].includes(el.parent.type));
						}

						return hoveredElements[0] || elements.find(el => ['BackWall'].includes(el.parent.type));
					} else {
						hoveredElements = elements.filter(el =>
							el.shapes.find(shape => shape.isPointOn(x, compare_y_point - y))
						);
					}
				}

				if (elements.every(el => !backWallsList.includes(el.parent.type))) {
					hoveredElements = elements.filter(el => basesList.includes(el.parent.type));
				}

				hoveredElements = hoveredElements ?
					hoveredElements.sort((a, b) => b.getBoundingZ().max - a.getBoundingZ().max) :
					[];

				return hoveredElements[0] || null;
			}
		}
	}

	getElementsRows(dependElement: Element): Array<Array<Element>> {
		if (this.cachedRows.has(dependElement)) {
			return this.cachedRows.get(dependElement);
		} else {
			const elements: Array<Element> = this.allElements
				.filter((element: Element) => element.depend === dependElement);

			// sort the elements first by y, then by x
			elements.sort((a: Element, b: Element) => {
				return b.getBoundingY().min - a.getBoundingY().min || a.getBoundingX().min - b.getBoundingX().min;
			});

			const rows: Array<Array<Element>> = [];
			let current: Array<Element> = [];

			for (const element of elements) {
				const tolerance: number = Utils.round(element.getBoundingY().min - current[0]?.getBoundingY().min || 0);

				if (current.length > 0 && Math.abs(tolerance) > GlobalSettings.ELEMENT_SIBLINGS_TOLERANCE) {
					rows.push(current);
					current = [];
				}

				current.push(element);
			}

			if (current.length > 0) rows.push(current);

			this.cachedRows.set(dependElement, rows);
			return rows;
		}
	}

	getShelfMeters(includeRounding: boolean = true): number {
		return this.availableElements
			.filter((element: Element) => element.parent.is(...shelvesList))
			.reduce((total: number, element: Element) => {
				let totalWidth: number;
				const baseWithCentralPillar: boolean = this.allElements
					.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar) && el.depend == element);
				const twoSided: boolean = element.depend?.parent.is(EquipmentElements.CentralPillar) || baseWithCentralPillar;

				if (includeRounding && element.isExtensionActive(ExtensionType.Rounding)) {
					const rounding: Extension<RoundingParams> = element.getExtension<RoundingParams>(ExtensionType.Rounding);
					const leftSideRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, element);
					const rightSideRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, element);
					const radiusSum: number = Utils.round(leftSideRadius + rightSideRadius);
					const width: number = Utils.round(element.Width - radiusSum + Math.PI * radiusSum / 2);

					totalWidth = twoSided ? width * 2 : width;
				} else {
					totalWidth = twoSided ? element.Width * 2 : element.Width;
				}

				return Utils.round(total + totalWidth);
			}, 0)
	}

	getHangBarMeters(): number {
		return this.availableElements
			.filter((element: Element) => element.parent.is(EquipmentElements.HangBar))
			.reduce((a: number, b: Element) => a + b.Width, 0);
	}

	getHooksMeters(): number {
		return this.allElements
			.filter((element: Element) => hookWallsList.includes(element.parent.type))
			.map((element: Element) => {
				const rows: Array<Array<Element>> = this.getElementsRows(element);
				return Utils.round(element.Width * rows.length);
			})
			.reduce((total: number, current: number) => Utils.round(total + current), 0);
	}

	getHolesMeters(): number {
		return this.allElements
			.filter((element: Element) => perforatedWallsList.includes(element.parent.type))
			.map((element: Element) => {
				const rows: Array<Array<Element>> = this.getElementsRows(element)
					.filter((row: Array<Element>) => row.some((element: Element) => element.products.length));
				return Utils.round(element.Width * rows.length);
			})
			.reduce((total: number, current: number) => Utils.round(total + current), 0);
	}

	getVerticalIntersectedElements(element: Element): Array<Element> {
		const maxYBounding: number = element.getBoundingY().max;

		return this.allElements
			.filter((el: Element) => el.parent.is(EquipmentElements.Partition, EquipmentElements.SideWalls))
			.filter((el: Element) => el.depend != element)
			.filter((el: Element) => {
				const boundingX: Bounding = el.getBoundingX();
				const intersectsLeftSide: boolean = element.shapes[0].isPointOn(boundingX.min);
				const intersectsRightSide: boolean = element.shapes[0].isPointOn(boundingX.max);
				const intersectsVertical: boolean = el.shapes.some((shape: Figure) => {
					return shape.isPointOn(null, maxYBounding) && maxYBounding < shape.getBoundingY().max;
				});

				return (intersectsLeftSide || intersectsRightSide) && intersectsVertical;
			});
	}

	get Shapes(): Array<Figure> {
		return Array.prototype.concat(...this.allElements.filter(el => el.parent.type !== 'Hole').map(el => el.shapes));
	}

	get Empty() {
		return !this.allElements.length;
	}

	get Width(): number {
		if (this.Empty) return 1;

		const elements: Array<Element> = this.allElements
			.filter((element: Element) => !element.parent.is(EquipmentElements.Hole));
		const max: number = Utils.max(elements.map((element: Element) => element.getBoundingX().max));
		const min: number = Utils.min(elements.map((element: Element) => element.getBoundingX().min));

		return Utils.round(max - min);
	}

	get Height(): number {
		if (this.Empty) return 1.5;

		const elements: Array<Element> = this.allElements
			.filter((element: Element) => !element.parent.is(EquipmentElements.Hole));

		return Utils.round(Utils.max(elements.map((element: Element) => element.getBoundingY().max)));
	}

	get Depth(): number {
		if (this.Empty) return 0.5;

		const elements: Array<Element> = this.allElements
			.filter((element: Element) => !element.parent.is(EquipmentElements.Hole));

		return Utils.round(Utils.max(elements.map((element: Element) => element.getBoundingZ().max)));
	}

	get LinearMeters() {
		const shelfMeters: number = this.getShelfMeters(),
			hangBarMeters: number = this.getHangBarMeters(),
			hookMeters: number = this.getHooksMeters(),
			holeMeters: number = this.getHolesMeters();

		return Utils.round(shelfMeters + hangBarMeters + hookMeters + holeMeters);
	}

	get SquareMeters() {
		return this.bases
			.map((base: Element) => base.shapes[0].square)
			.reduce((a: number, b: number) => Utils.round(a + b), 0);
	}
}